/**
 * PR-E3 (spec financiele-afscherming §9.2) — gedeelde rechtenmatrix
 *
 * Bron-of-truth voor de default-permissies per `UserRole`. Wordt zowel
 * door de backoffice-UI (rechtenmatrix bewerken, "Reset naar role"-knop)
 * als door de API (auto-seed bij user-aanmaak, `reset-to-role`-endpoint)
 * gebruikt. Voorheen alleen aanwezig in `apps/backoffice/src/lib/module-
 * permissions.ts`; hier verplaatst zodat beide kanten dezelfde matrix
 * gebruiken.
 *
 * De backoffice re-exporteert deze constants via een dunne wrapper zodat
 * bestaande imports (`@/lib/module-permissions`) blijven werken.
 */

export const MODULES = [
  'MEDEWERKERS',
  'CRM',
  'PLANNING',
  'ASSETS',
  'BRUIKLEEN',
  'VERLOF',
  'VERZUIM',
  'REISAFSTANDEN',
  'UREN',
  'DECLARATIES',
  'DOCUMENTEN',
  'NOTIFICATIES',
  'PAYROLL',
  'ENTITEITEN',
  'OFFERTES',
  // Ronde 4 W6 — `ORDERS` verwijderd; Order-model bestaat niet meer.
  'WERKBONNEN',
  'FACTUREN',
  'SELFSERVICE',
  'KALENDER',
  'COMMUNICATIE',
  'RAPPORTAGE',
  'VOERTUIGEN',
  'ONBOARDING',
  'INTERCOMPANY',
  'TAKEN',
  'IMPERSONATION',
  // FO-303-P5 — `DIENSTEN` verwijderd uit de actieve module-set; de DienstenPlein-
  // module is weg. De Prisma-enumwaarde blijft (geen DROP VALUE in Postgres) maar
  // is dood, zoals ORDERS/TARGETING; de diensten-catalogus-reads draaien op requireAuth.
  'FORMULIEREN',
  'INKOOP',
  'KWALITEIT',
  'PROJECTEN',
  // Batch-7 — `TARGETING` hernoemd naar ACQUISITIE; de Prisma-enum-waarde
  // blijft bestaan (geen DROP VALUE in Postgres) maar is dood, zoals ORDERS.
  'KLANTENPORTAAL',
  'MAIL',
  'IMPORT',
  'CONSIGNATIE',
  'ACQUISITIE',
  'EXPORT',
  'AI_RAPPORTAGE',
  'KEUZEBUDGET',
  // Track-A (postvak-in-module spec) — centraal Postvak In met persoon/functie-routing.
  'POSTVAK_IN',
  // PR-C (voorraad-magazijn spec) — module-permissie voor Voorraad/Magazijn.
  'VOORRAAD',
  // Track-H quickwins-p2 — externe integraties (API-keys + expiry-reminders).
  // Admin-only; geen lower-level rollen krijgen zicht op API-key metadata.
  'INTEGRATIES',
  // Spec D §SS21 — Opleidingen (vervangt Course-module).
  'OPLEIDINGEN',
  // Ronde 4 W5 — Trends & Analyse module (spec ronde-4/05-trends-analyse-module.md).
  'TRENDS',
  // Spec skillmatrix-v2 — Skill Matrix module (categorieën, skills, medewerker-scores).
  'SKILLS',
  // Kledingmodule — punten-gestuurde kledingbestellingen + teamleider-beoordeling.
  'KLEDING',
  // Veiligheidsmodule — LMRA/TRA/LOTOTO checklists, werkinstructies, medewerker-documentatie.
  'VEILIGHEID',
  // Uitrustingmodule — credit-gestuurde uitrustingbestellingen + Van Ysselt-koppeling.
  'UITRUSTING',
  // Ongevallen-/incidentenregistratie (HR/TL 5-fasen-workflow, audit-trail, verzuim-koppeling).
  'ONGEVALLEN',
  // NHG werkgeversverklaring (lane O1) — genereren/controleren/ondertekenen/verstrekken.
  'WERKGEVERSVERKLARING',
  // Spec 34 — Operationele takenmodule (backlog, klein planbord, slimme taaktypes,
  // escalatie). Eigen rechten, los van de aggregator-`TAKEN` en van `PLANNING`.
  'OPERATIONELE_TAKEN',
  // Batch-4 calc — Calculatie & Kostprijs-module (kostprijs medewerkers,
  // calculatie maken, planning-/historische-/scenario-calculatie, advies, gate).
  'CALCULATIE',
  // Batch-7 recruitment — sollicitanten/gesprekken/aanbiedingen/talentpool.
  // Kandidaatdata = persoonsgegevens: default alleen HR/ADMIN volledig.
  'RECRUITMENT',
  // Spec 37 — certificaat-aanvragen: medewerker dient in, TL/HR/ADMIN keuren goed.
  'CERTIFICATEN',
  // Batch-12 FO-001 — Wachtwoordkluis / credential vault. Sinds spec 224 toegang
  // PER PERSOON per geheim: lezen is afgeleid (wie bij minstens één geheim mag,
  // ziet de kluis), schrijven = nieuwe geheimen aanmaken, volledig = beheerder
  // (ziet alles, regelt de toegang). Geen rol krijgt dit standaard.
  'KLUIS',
  // FO-033 — Ops-rapportage: instellen + live dashboard + verzend-historie van de
  // geautomatiseerde ops-rapportage (App Insights/kosten/back-up). Backoffice-only.
  'OPS_RAPPORTAGE',
  // FO-050 (batch-16, security) — Autorisatiebeheer (rollen/module-permissies/
  // toegang-flags/permissie-templates). Default ALLEEN ADMIN; los van
  // MEDEWERKERS.canEdit dat enkel medewerker-gegevens dekt.
  'AUTORISATIE',
  // FO-059 (batch-18) — Apparatuur (laptops/telefoons/tablets/simkaarten op de
  // device-assets). Los toekenbaar recht voor IT/facilitair; los van ASSETS/VOORRAAD.
  'APPARATUUR',
  // FO-096 (batch-21) — submodule-keys. Fijnmazigere rechten bovenop de
  // moeder-module; defaults erven de moeder-module (zie DEFAULT_ROLE_PERMISSIONS).
  // WERKBONNEN_FACTURATIE splitst alleen canInvoice van WERKBONNEN af; de
  // werkbon-facturatie-endpoints eisen voortaan WERKBONNEN_FACTURATIE.canInvoice.
  'WERKBONNEN_FACTURATIE',
  // MEDEWERKERS_LOON — loon-/facilitiesblok binnen MEDEWERKERS, los toekenbaar.
  'MEDEWERKERS_LOON',
  // NEN3140 (FO-323, batch-36) — NEN 3140-aanwijzingen (veiligheid/personeel).
  'NEN3140',
  // batch-68 gespreksvoorbereiding (FO-346 §4) — submodule-recht voor
  // vertrouwelijke HR-notities. Default alleen HR (schrijven) en ADMIN (volledig).
  'HR_VERTROUWELIJK',
] as const

export type Module = (typeof MODULES)[number]

export const LEVELS = ['geen', 'lezen', 'schrijven', 'volledig'] as const
export type Level = (typeof LEVELS)[number]

export type ModuleFlags = {
  canView: boolean
  canEdit: boolean
  canDelete: boolean
  canApprove: boolean
  canInvoice: boolean
}

export const levelToFlags = (level: Level): ModuleFlags => {
  switch (level) {
    case 'geen':
      return { canView: false, canEdit: false, canDelete: false, canApprove: false, canInvoice: false }
    case 'lezen':
      return { canView: true, canEdit: false, canDelete: false, canApprove: false, canInvoice: false }
    case 'schrijven':
      return { canView: true, canEdit: true, canDelete: false, canApprove: false, canInvoice: false }
    case 'volledig':
      return { canView: true, canEdit: true, canDelete: true, canApprove: true, canInvoice: true }
  }
}

export const flagsToLevel = (flags: Partial<ModuleFlags>): Level => {
  if (flags.canDelete || flags.canApprove || flags.canInvoice) return 'volledig'
  if (flags.canEdit) return 'schrijven'
  if (flags.canView) return 'lezen'
  return 'geen'
}

export const USER_ROLES = [
  'MONTEUR',
  'TEAMLEIDER',
  'PLANNER',
  'HR',
  'SALARIS',
  'MANAGER',
  'ADMIN',
  // batch-50 accountant-rol (FO-404) — vergrendeld alleen-lezen profiel voor de
  // externe accountant. De defaults hieronder zijn de bovengrens én de
  // ondergrens: de allowlist-klem (ACCOUNTANT_ALLOWLIST in @streep/types) zet
  // álles buiten FACTUREN/INKOOP/DECLARATIES canView server-side op false.
  'ACCOUNTANT',
] as const

export type UserRole = (typeof USER_ROLES)[number]

/**
 * Default permissie-matrix per rol. Best-effort mapping van de Streep
 * rechtenmatrix naar onze module-set; per-user override blijft mogelijk
 * via de rechtenmatrix-UI.
 *
 * Spiegelt de matrix uit `apps/backoffice/src/lib/module-permissions.ts`
 * 1:1 — wijzig hier en check beide consumers.
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, Record<Module, Level>> = {
  MONTEUR: {
    // FO-050 — rechtenbeheer is ADMIN-only; alleen ADMIN krijgt AUTORISATIE.
    AUTORISATIE: 'geen',
    // Spec 224: kluistoegang alleen per persoon, nooit via de rol.
    KLUIS: 'geen',
    CALCULATIE: 'geen',
    RECRUITMENT: 'geen',
    CERTIFICATEN: 'geen',
    MEDEWERKERS: 'geen',
    CRM: 'geen',
    PLANNING: 'geen',
    ASSETS: 'geen',
    BRUIKLEEN: 'geen',
    VERLOF: 'schrijven',
    VERZUIM: 'schrijven',
    REISAFSTANDEN: 'lezen',
    UREN: 'schrijven',
    DECLARATIES: 'schrijven',
    DOCUMENTEN: 'lezen',
    NOTIFICATIES: 'schrijven',
    PAYROLL: 'geen',
    ENTITEITEN: 'geen',
    OFFERTES: 'geen',
    // Ronde 4 W6 — ORDERS verwijderd uit role-defaults.
    WERKBONNEN: 'schrijven',
    FACTUREN: 'geen',
    SELFSERVICE: 'schrijven',
    KALENDER: 'schrijven',
    COMMUNICATIE: 'lezen',
    RAPPORTAGE: 'geen',
    VOERTUIGEN: 'lezen',
    ONBOARDING: 'geen',
    INTERCOMPANY: 'geen',
    TAKEN: 'lezen',
    IMPERSONATION: 'geen',
    FORMULIEREN: 'schrijven',
    INKOOP: 'geen',
    KWALITEIT: 'lezen',
    PROJECTEN: 'geen',
    KLANTENPORTAAL: 'geen',
    MAIL: 'geen',
    IMPORT: 'geen',
    CONSIGNATIE: 'geen',
    ACQUISITIE: 'geen',
    EXPORT: 'geen',
    AI_RAPPORTAGE: 'geen',
    KEUZEBUDGET: 'schrijven',
    POSTVAK_IN: 'geen',
    VOORRAAD: 'geen',
    INTEGRATIES: 'geen',
    OPLEIDINGEN: 'geen',
    TRENDS: 'geen',
    SKILLS: 'geen',
    KLEDING: 'schrijven',
    VEILIGHEID: 'schrijven',
    UITRUSTING: 'schrijven',
    ONGEVALLEN: 'geen',
    WERKGEVERSVERKLARING: 'geen',
    OPERATIONELE_TAKEN: 'lezen',
    OPS_RAPPORTAGE: 'geen',
    APPARATUUR: 'geen',
    // FO-096 — submodule-keys erven hun moeder-module (geen rechten-wijziging).
    WERKBONNEN_FACTURATIE: 'schrijven', // = WERKBONNEN
    MEDEWERKERS_LOON: 'geen', // = MEDEWERKERS
    NEN3140: 'geen', // FO-323 (batch-36)
    HR_VERTROUWELIJK: 'geen', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
  TEAMLEIDER: {
    AUTORISATIE: 'geen',
    KLUIS: 'geen', // spec 224: alleen per persoon
    CALCULATIE: 'volledig',
    RECRUITMENT: 'geen',
    CERTIFICATEN: 'volledig',
    MEDEWERKERS: 'lezen',
    CRM: 'geen',
    PLANNING: 'schrijven',
    ASSETS: 'lezen',
    BRUIKLEEN: 'lezen',
    VERLOF: 'volledig',
    VERZUIM: 'volledig',
    REISAFSTANDEN: 'lezen',
    UREN: 'volledig',
    DECLARATIES: 'volledig',
    DOCUMENTEN: 'schrijven',
    NOTIFICATIES: 'schrijven',
    PAYROLL: 'geen',
    ENTITEITEN: 'geen',
    OFFERTES: 'geen',
    // Ronde 4 W6 — ORDERS verwijderd uit role-defaults.
    WERKBONNEN: 'volledig',
    FACTUREN: 'volledig',
    SELFSERVICE: 'lezen',
    KALENDER: 'schrijven',
    COMMUNICATIE: 'schrijven',
    RAPPORTAGE: 'lezen',
    VOERTUIGEN: 'lezen',
    ONBOARDING: 'lezen',
    INTERCOMPANY: 'geen',
    TAKEN: 'lezen',
    IMPERSONATION: 'volledig',
    FORMULIEREN: 'volledig',
    INKOOP: 'lezen',
    KWALITEIT: 'schrijven',
    PROJECTEN: 'lezen',
    KLANTENPORTAAL: 'lezen',
    MAIL: 'geen',
    IMPORT: 'geen',
    CONSIGNATIE: 'lezen',
    // batch-7: erft het oude TARGETING-niveau (lezen) na de hernoeming.
    ACQUISITIE: 'lezen',
    EXPORT: 'lezen',
    AI_RAPPORTAGE: 'lezen',
    KEUZEBUDGET: 'lezen',
    POSTVAK_IN: 'schrijven',
    VOORRAAD: 'lezen',
    INTEGRATIES: 'geen',
    OPLEIDINGEN: 'lezen',
    TRENDS: 'lezen',
    SKILLS: 'schrijven',
    KLEDING: 'volledig',
    VEILIGHEID: 'lezen',
    UITRUSTING: 'volledig',
    ONGEVALLEN: 'schrijven',
    WERKGEVERSVERKLARING: 'geen',
    OPERATIONELE_TAKEN: 'volledig',
    OPS_RAPPORTAGE: 'geen',
    APPARATUUR: 'lezen',
    WERKBONNEN_FACTURATIE: 'volledig', // = WERKBONNEN
    MEDEWERKERS_LOON: 'lezen', // = MEDEWERKERS
    NEN3140: 'lezen', // FO-323 (batch-36)
    HR_VERTROUWELIJK: 'geen', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
  PLANNER: {
    AUTORISATIE: 'geen',
    // Spec 224: kluistoegang alleen per persoon, nooit via de rol.
    KLUIS: 'geen',
    CALCULATIE: 'schrijven',
    RECRUITMENT: 'geen',
    CERTIFICATEN: 'geen',
    MEDEWERKERS: 'lezen',
    CRM: 'geen',
    PLANNING: 'volledig',
    ASSETS: 'lezen',
    BRUIKLEEN: 'lezen',
    VERLOF: 'geen',
    VERZUIM: 'lezen',
    REISAFSTANDEN: 'schrijven',
    UREN: 'lezen',
    DECLARATIES: 'geen',
    DOCUMENTEN: 'lezen',
    NOTIFICATIES: 'schrijven',
    PAYROLL: 'geen',
    ENTITEITEN: 'geen',
    OFFERTES: 'geen',
    // Ronde 4 W6 — ORDERS verwijderd uit role-defaults.
    WERKBONNEN: 'schrijven',
    FACTUREN: 'geen',
    SELFSERVICE: 'geen',
    KALENDER: 'schrijven',
    COMMUNICATIE: 'schrijven',
    RAPPORTAGE: 'lezen',
    VOERTUIGEN: 'schrijven',
    ONBOARDING: 'geen',
    INTERCOMPANY: 'schrijven',
    TAKEN: 'lezen',
    IMPERSONATION: 'geen',
    FORMULIEREN: 'lezen',
    INKOOP: 'schrijven',
    KWALITEIT: 'lezen',
    PROJECTEN: 'volledig',
    KLANTENPORTAAL: 'schrijven',
    MAIL: 'geen',
    IMPORT: 'geen',
    CONSIGNATIE: 'volledig',
    ACQUISITIE: 'volledig',
    EXPORT: 'volledig',
    AI_RAPPORTAGE: 'lezen',
    KEUZEBUDGET: 'geen',
    POSTVAK_IN: 'schrijven',
    VOORRAAD: 'volledig',
    INTEGRATIES: 'geen',
    OPLEIDINGEN: 'lezen',
    TRENDS: 'lezen',
    SKILLS: 'lezen',
    KLEDING: 'geen',
    VEILIGHEID: 'lezen',
    UITRUSTING: 'geen',
    ONGEVALLEN: 'geen',
    WERKGEVERSVERKLARING: 'geen',
    OPERATIONELE_TAKEN: 'volledig',
    OPS_RAPPORTAGE: 'geen',
    APPARATUUR: 'lezen',
    WERKBONNEN_FACTURATIE: 'schrijven', // = WERKBONNEN
    MEDEWERKERS_LOON: 'lezen', // = MEDEWERKERS
    NEN3140: 'lezen', // FO-323 (batch-36)
    HR_VERTROUWELIJK: 'geen', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
  HR: {
    AUTORISATIE: 'geen',
    // Spec 224: kluistoegang alleen per persoon, nooit via de rol.
    KLUIS: 'geen',
    CALCULATIE: 'lezen',
    RECRUITMENT: 'volledig',
    CERTIFICATEN: 'volledig',
    MEDEWERKERS: 'volledig',
    CRM: 'geen',
    PLANNING: 'geen',
    ASSETS: 'geen',
    BRUIKLEEN: 'geen',
    VERLOF: 'volledig',
    VERZUIM: 'volledig',
    REISAFSTANDEN: 'lezen',
    UREN: 'volledig',
    DECLARATIES: 'volledig',
    DOCUMENTEN: 'volledig',
    NOTIFICATIES: 'lezen',
    PAYROLL: 'lezen',
    ENTITEITEN: 'lezen',
    OFFERTES: 'geen',
    // Ronde 4 W6 — ORDERS verwijderd uit role-defaults.
    WERKBONNEN: 'geen',
    FACTUREN: 'volledig',
    SELFSERVICE: 'volledig',
    KALENDER: 'volledig',
    COMMUNICATIE: 'volledig',
    RAPPORTAGE: 'volledig',
    VOERTUIGEN: 'lezen',
    ONBOARDING: 'volledig',
    INTERCOMPANY: 'lezen',
    TAKEN: 'lezen',
    IMPERSONATION: 'volledig',
    FORMULIEREN: 'volledig',
    INKOOP: 'geen',
    KWALITEIT: 'geen',
    PROJECTEN: 'geen',
    KLANTENPORTAAL: 'geen',
    MAIL: 'schrijven',
    IMPORT: 'geen',
    CONSIGNATIE: 'geen',
    // batch-7: erft het oude TARGETING-niveau (volledig) na de hernoeming.
    ACQUISITIE: 'volledig',
    EXPORT: 'volledig',
    AI_RAPPORTAGE: 'lezen',
    KEUZEBUDGET: 'volledig',
    POSTVAK_IN: 'schrijven',
    VOORRAAD: 'schrijven',
    INTEGRATIES: 'geen',
    OPLEIDINGEN: 'volledig',
    TRENDS: 'lezen',
    SKILLS: 'volledig',
    KLEDING: 'volledig',
    VEILIGHEID: 'volledig',
    UITRUSTING: 'volledig',
    ONGEVALLEN: 'volledig',
    WERKGEVERSVERKLARING: 'volledig',
    OPERATIONELE_TAKEN: 'schrijven',
    OPS_RAPPORTAGE: 'geen',
    // HR drijft onboarding/offboarding-middelen + retourcheck → kan toewijzen/innemen.
    APPARATUUR: 'schrijven',
    WERKBONNEN_FACTURATIE: 'geen', // = WERKBONNEN
    MEDEWERKERS_LOON: 'volledig', // = MEDEWERKERS
    NEN3140: 'volledig', // FO-323 (batch-36)
    HR_VERTROUWELIJK: 'schrijven', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
  SALARIS: {
    AUTORISATIE: 'geen',
    // Geen kluis-toegang by default (least privilege); via rechtenmatrix te geven.
    KLUIS: 'geen',
    CALCULATIE: 'lezen',
    RECRUITMENT: 'geen',
    CERTIFICATEN: 'geen',
    MEDEWERKERS: 'lezen',
    CRM: 'geen',
    PLANNING: 'geen',
    ASSETS: 'geen',
    BRUIKLEEN: 'geen',
    VERLOF: 'geen',
    VERZUIM: 'lezen',
    REISAFSTANDEN: 'lezen',
    UREN: 'volledig',
    DECLARATIES: 'volledig',
    DOCUMENTEN: 'lezen',
    NOTIFICATIES: 'schrijven',
    PAYROLL: 'volledig',
    ENTITEITEN: 'geen',
    OFFERTES: 'geen',
    // Ronde 4 W6 — ORDERS verwijderd uit role-defaults.
    WERKBONNEN: 'geen',
    FACTUREN: 'volledig',
    SELFSERVICE: 'lezen',
    KALENDER: 'lezen',
    COMMUNICATIE: 'lezen',
    RAPPORTAGE: 'lezen',
    VOERTUIGEN: 'geen',
    ONBOARDING: 'lezen',
    INTERCOMPANY: 'geen',
    TAKEN: 'lezen',
    IMPERSONATION: 'geen',
    FORMULIEREN: 'lezen',
    INKOOP: 'volledig',
    KWALITEIT: 'geen',
    PROJECTEN: 'lezen',
    KLANTENPORTAAL: 'geen',
    MAIL: 'geen',
    IMPORT: 'geen',
    CONSIGNATIE: 'geen',
    ACQUISITIE: 'geen',
    EXPORT: 'lezen',
    AI_RAPPORTAGE: 'lezen',
    KEUZEBUDGET: 'lezen',
    POSTVAK_IN: 'schrijven',
    VOORRAAD: 'lezen',
    INTEGRATIES: 'geen',
    OPLEIDINGEN: 'lezen',
    TRENDS: 'lezen',
    SKILLS: 'geen',
    KLEDING: 'geen',
    VEILIGHEID: 'geen',
    UITRUSTING: 'geen',
    ONGEVALLEN: 'geen',
    WERKGEVERSVERKLARING: 'lezen',
    OPERATIONELE_TAKEN: 'geen',
    OPS_RAPPORTAGE: 'geen',
    APPARATUUR: 'geen',
    WERKBONNEN_FACTURATIE: 'geen', // = WERKBONNEN
    MEDEWERKERS_LOON: 'lezen', // = MEDEWERKERS
    NEN3140: 'geen', // FO-323 (batch-36)
    HR_VERTROUWELIJK: 'geen', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
  MANAGER: {
    // FO-050 — management beheert NIET de autorisatie; alleen ADMIN.
    AUTORISATIE: 'geen',
    // Spec 224: kluistoegang alleen per persoon, nooit via de rol.
    KLUIS: 'geen',
    CALCULATIE: 'volledig',
    RECRUITMENT: 'geen',
    CERTIFICATEN: 'geen',
    MEDEWERKERS: 'lezen',
    CRM: 'lezen',
    PLANNING: 'lezen',
    ASSETS: 'lezen',
    BRUIKLEEN: 'lezen',
    VERLOF: 'volledig',
    VERZUIM: 'volledig',
    REISAFSTANDEN: 'lezen',
    UREN: 'volledig',
    DECLARATIES: 'volledig',
    DOCUMENTEN: 'lezen',
    NOTIFICATIES: 'schrijven',
    PAYROLL: 'lezen',
    ENTITEITEN: 'lezen',
    OFFERTES: 'schrijven',
    // Ronde 4 W6 — ORDERS verwijderd uit role-defaults.
    WERKBONNEN: 'volledig',
    FACTUREN: 'volledig',
    SELFSERVICE: 'lezen',
    KALENDER: 'schrijven',
    COMMUNICATIE: 'volledig',
    RAPPORTAGE: 'volledig',
    VOERTUIGEN: 'lezen',
    ONBOARDING: 'lezen',
    INTERCOMPANY: 'volledig',
    TAKEN: 'lezen',
    IMPERSONATION: 'schrijven',
    FORMULIEREN: 'volledig',
    INKOOP: 'schrijven',
    KWALITEIT: 'schrijven',
    PROJECTEN: 'volledig',
    KLANTENPORTAAL: 'schrijven',
    MAIL: 'lezen',
    IMPORT: 'geen',
    CONSIGNATIE: 'volledig',
    ACQUISITIE: 'volledig',
    EXPORT: 'volledig',
    AI_RAPPORTAGE: 'volledig',
    KEUZEBUDGET: 'volledig',
    POSTVAK_IN: 'volledig',
    VOORRAAD: 'volledig',
    INTEGRATIES: 'geen',
    OPLEIDINGEN: 'volledig',
    TRENDS: 'volledig',
    SKILLS: 'geen',
    KLEDING: 'volledig',
    VEILIGHEID: 'volledig',
    UITRUSTING: 'lezen',
    ONGEVALLEN: 'volledig',
    WERKGEVERSVERKLARING: 'volledig',
    OPERATIONELE_TAKEN: 'volledig',
    OPS_RAPPORTAGE: 'geen',
    APPARATUUR: 'volledig',
    WERKBONNEN_FACTURATIE: 'volledig', // = WERKBONNEN
    MEDEWERKERS_LOON: 'lezen', // = MEDEWERKERS
    NEN3140: 'lezen', // FO-323 (batch-36)
    HR_VERTROUWELIJK: 'geen', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
  ADMIN: {
    // FO-050 — ALLEEN ADMIN beheert rechten/rollen/toegang (canView + canEdit).
    AUTORISATIE: 'volledig',
    KLUIS: 'geen', // spec 224: alleen per persoon
    CALCULATIE: 'volledig',
    RECRUITMENT: 'volledig',
    CERTIFICATEN: 'volledig',
    MEDEWERKERS: 'volledig',
    CRM: 'volledig',
    PLANNING: 'volledig',
    ASSETS: 'volledig',
    BRUIKLEEN: 'volledig',
    VERLOF: 'volledig',
    VERZUIM: 'volledig',
    REISAFSTANDEN: 'volledig',
    UREN: 'volledig',
    DECLARATIES: 'volledig',
    DOCUMENTEN: 'volledig',
    NOTIFICATIES: 'volledig',
    PAYROLL: 'volledig',
    ENTITEITEN: 'volledig',
    OFFERTES: 'volledig',
    // Ronde 4 W6 — ORDERS verwijderd uit role-defaults.
    WERKBONNEN: 'volledig',
    FACTUREN: 'volledig',
    SELFSERVICE: 'volledig',
    KALENDER: 'volledig',
    COMMUNICATIE: 'volledig',
    RAPPORTAGE: 'volledig',
    VOERTUIGEN: 'volledig',
    ONBOARDING: 'volledig',
    INTERCOMPANY: 'volledig',
    TAKEN: 'volledig',
    IMPERSONATION: 'volledig',
    FORMULIEREN: 'volledig',
    INKOOP: 'volledig',
    KWALITEIT: 'volledig',
    PROJECTEN: 'volledig',
    KLANTENPORTAAL: 'volledig',
    MAIL: 'volledig',
    IMPORT: 'volledig',
    CONSIGNATIE: 'volledig',
    ACQUISITIE: 'volledig',
    EXPORT: 'volledig',
    AI_RAPPORTAGE: 'volledig',
    KEUZEBUDGET: 'volledig',
    POSTVAK_IN: 'volledig',
    VOORRAAD: 'volledig',
    INTEGRATIES: 'volledig',
    OPLEIDINGEN: 'volledig',
    TRENDS: 'volledig',
    SKILLS: 'volledig',
    KLEDING: 'volledig',
    VEILIGHEID: 'volledig',
    UITRUSTING: 'volledig',
    ONGEVALLEN: 'volledig',
    WERKGEVERSVERKLARING: 'volledig',
    OPERATIONELE_TAKEN: 'volledig',
    OPS_RAPPORTAGE: 'volledig',
    APPARATUUR: 'volledig',
    WERKBONNEN_FACTURATIE: 'volledig', // = WERKBONNEN
    MEDEWERKERS_LOON: 'volledig', // = MEDEWERKERS
    NEN3140: 'volledig', // FO-323 (batch-36)
    HR_VERTROUWELIJK: 'volledig', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
  // batch-50 accountant-rol (FO-404) — vast profiel: EXACT drie modules op
  // 'lezen', al het overige 'geen'. Deze matrix is bewust identiek aan de
  // ACCOUNTANT_ALLOWLIST; wijzigt iemand hier iets, dan klemt de server het
  // alsnog weg (de allowlist wint altijd). PAYROLL blijft expliciet 'geen'
  // (spec §4 + A3: loonstroken/payroll buiten scope).
  ACCOUNTANT: {
    AUTORISATIE: 'geen',
    KLUIS: 'geen',
    CALCULATIE: 'geen',
    RECRUITMENT: 'geen',
    CERTIFICATEN: 'geen',
    MEDEWERKERS: 'geen',
    CRM: 'geen',
    PLANNING: 'geen',
    ASSETS: 'geen',
    BRUIKLEEN: 'geen',
    VERLOF: 'geen',
    VERZUIM: 'geen',
    REISAFSTANDEN: 'geen',
    UREN: 'geen',
    DECLARATIES: 'lezen',
    DOCUMENTEN: 'geen',
    NOTIFICATIES: 'geen',
    PAYROLL: 'geen',
    ENTITEITEN: 'geen',
    OFFERTES: 'geen',
    WERKBONNEN: 'geen',
    FACTUREN: 'lezen',
    SELFSERVICE: 'geen',
    KALENDER: 'geen',
    COMMUNICATIE: 'geen',
    RAPPORTAGE: 'geen',
    VOERTUIGEN: 'geen',
    ONBOARDING: 'geen',
    INTERCOMPANY: 'geen',
    TAKEN: 'geen',
    IMPERSONATION: 'geen',
    FORMULIEREN: 'geen',
    INKOOP: 'lezen',
    KWALITEIT: 'geen',
    PROJECTEN: 'geen',
    KLANTENPORTAAL: 'geen',
    MAIL: 'geen',
    IMPORT: 'geen',
    CONSIGNATIE: 'geen',
    ACQUISITIE: 'geen',
    EXPORT: 'geen',
    AI_RAPPORTAGE: 'geen',
    KEUZEBUDGET: 'geen',
    POSTVAK_IN: 'geen',
    VOORRAAD: 'geen',
    INTEGRATIES: 'geen',
    OPLEIDINGEN: 'geen',
    TRENDS: 'geen',
    SKILLS: 'geen',
    KLEDING: 'geen',
    VEILIGHEID: 'geen',
    UITRUSTING: 'geen',
    ONGEVALLEN: 'geen',
    WERKGEVERSVERKLARING: 'geen',
    OPERATIONELE_TAKEN: 'geen',
    OPS_RAPPORTAGE: 'geen',
    APPARATUUR: 'geen',
    WERKBONNEN_FACTURATIE: 'geen', // = WERKBONNEN
    MEDEWERKERS_LOON: 'geen', // = MEDEWERKERS
    NEN3140: 'geen',
    HR_VERTROUWELIJK: 'geen', // batch-68 gespreksvoorbereiding (FO-346 §4)
  },
}

/**
 * Module-groepering voor de admin-rechtenmatrix UI (spec §10.2). De UI
 * rendert per groep een sub-header en daaronder de modules van die groep.
 * Modules die niet in een groep voorkomen worden onder "Overig"
 * gerenderd (UI-fallback).
 */
export const MODULE_GROEPEN: Record<string, ReadonlyArray<Module>> = {
  // Ronde 4 W6 — ORDERS uit Operatie-groep verwijderd.
  // FO-303-P5 — `DIENSTEN` uit Operatie-groep verwijderd (module weg).
  Operatie: ['PLANNING', 'OPERATIONELE_TAKEN', 'WERKBONNEN', 'WERKBONNEN_FACTURATIE', 'CRM', 'PROJECTEN', 'BRUIKLEEN', 'ASSETS', 'VOERTUIGEN', 'APPARATUUR', 'CONSIGNATIE', 'KWALITEIT', 'VEILIGHEID'],
  Financieel: ['FACTUREN', 'PAYROLL', 'DECLARATIES', 'OFFERTES', 'INKOOP', 'INTERCOMPANY', 'KEUZEBUDGET', 'CALCULATIE'],
  Personeel: ['MEDEWERKERS', 'MEDEWERKERS_LOON', 'HR_VERTROUWELIJK', 'VERLOF', 'VERZUIM', 'UREN', 'ONBOARDING', 'FORMULIEREN', 'REISAFSTANDEN', 'SELFSERVICE', 'SKILLS', 'KLEDING', 'UITRUSTING', 'ONGEVALLEN', 'WERKGEVERSVERKLARING', 'RECRUITMENT', 'CERTIFICATEN', 'NEN3140'],
  Communicatie: ['NOTIFICATIES', 'COMMUNICATIE', 'KALENDER', 'MAIL', 'TAKEN', 'KLANTENPORTAAL'],
  Sales: ['ACQUISITIE'],
  Rapportage: ['RAPPORTAGE', 'AI_RAPPORTAGE', 'TRENDS', 'EXPORT', 'IMPORT', 'DOCUMENTEN'],
  Instellingen: ['ENTITEITEN', 'IMPERSONATION', 'INTEGRATIES', 'KLUIS', 'OPS_RAPPORTAGE', 'AUTORISATIE'],
}
